/**
 * @ComponentFor PageNotFoundViewModel
 */
import React from 'react';
import connect from 'Shared/connect';
import { translate } from '@polarnopyret/scope';
import { ContentArea } from '@polarnopyret/scope-episerver';
import Viewport from 'Shared/Viewport';
import Page from 'Shared/Page';
import { styled } from '@glitz/react';
import { HEIGHT as DESKTOP_HEADER_HEIGHT } from 'SiteLayout/Header/DesktopHeader';
import { BLACK_TOP_MOBILE_HEIGHT, BLACK_TOP_DESKTOP_HEIGHT } from 'SiteLayout/Header';
import { HEIGHT as MOBILE_HEIGHT } from 'SiteLayout/Header/MobileHeader';
import { QUICKSEARCH_HEIGHT } from 'Search/QuickSearch';
import { H1, Paragraph, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { setImageBackground, Preset } from 'Shared/image-background';
import * as style from 'Shared/Style';

type ConnectStateType = {
  pageNotFoundBlocks: Scope.ContentArea;
  pageNotFoundIntro: string;
  pageNotFoundImage: Scope.UrlViewModel;
};

type PropType = {} & ConnectStateType;

const TopContentWrap = styled.div({
  overflow: 'hidden',
  background: {
    size: 'cover',
    positionX: 'center',
    positionY: 'center',
  },
  height: '100vh',
})

const IntroWrap = styled.div({
  margin: { y: '40px', },
  ['@media ' + style.mediaMinLarge]: {
    margin: { y: '80px', },
  },
})

const CenterContent = styled.div({
  textAlign: 'center',
})

const PageNotFound = (props: PropType) => {
  const epiContent = props.pageNotFoundBlocks;
  const topMarginCompact = QUICKSEARCH_HEIGHT + MOBILE_HEIGHT + BLACK_TOP_MOBILE_HEIGHT;
  const topMarginDesktop = DESKTOP_HEADER_HEIGHT + BLACK_TOP_DESKTOP_HEIGHT;

  return (
    <Viewport>
      {(isCompact) => (
        <Page>
          {!epiContent ? (
            <GridWrap className='grid' css={{ paddingTop: '48px', paddingBottom: '96px' }}>
              <CenterContent className={createGridClasses(12, 12)}>
                <H1 fontSize={ResponsiveSize.D96_M40}>404</H1>
                <Paragraph css={{ marginTop: '24px' }} fontSize={ResponsiveSize.D20_M20}>{translate('/site/errors/pagenotfoundtext')}</Paragraph>
              </CenterContent>
            </GridWrap>
          ) : (
            <>
              <TopContentWrap
                css={{
                  marginTop: isCompact ? -topMarginCompact : -topMarginDesktop,
                  background: {
                    image: setImageBackground(props.pageNotFoundImage?.url, Preset.FullWidth),
                  },
                }}
              >
                <GridWrap className='grid' css={{ height: '100%', alignItems: 'center', ...(isCompact && { position: 'relative' }) }}>
                  <div className={createGridClasses(12, 10, isCompact ? '' : createOffsetGridClassLarge(2) + ' grid subgrid-10')}>
                    <CenterContent className={createGridClasses(12, 8, createOffsetGridClassLarge(2))}>
                      <H1 css={{ color: style.colors.monochrome.white }} fontSize={ResponsiveSize.D96_M40}>404</H1>
                      <Paragraph fontSize={ResponsiveSize.D20_M20}>{props.pageNotFoundIntro}</Paragraph>
                    </CenterContent>
                  </div>
                </GridWrap>
              </TopContentWrap>
              {props.pageNotFoundIntro && (
                <GridWrap className='grid'>
                  <IntroWrap className={createGridClasses(12, 8, createOffsetGridClassLarge(2))}>
                    <Paragraph fontSize={ResponsiveSize.D32_M24}>{props.pageNotFoundIntro}</Paragraph>
                  </IntroWrap>
                </GridWrap>
              )}
              <ContentArea {...props.pageNotFoundBlocks} />
            </>
          )
          }
        </Page>
      )}
    </Viewport>
  );
}

export default connect(
  (state): ConnectStateType => ({
    pageNotFoundBlocks: state.appShellData.content.pageNotFoundBlocks,
    pageNotFoundIntro: state.appShellData.content.pageNotFoundIntro,
    pageNotFoundImage: state.appShellData.content.pageNotFoundImage,
  }),
)(PageNotFound);
